import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, Form, Spinner } from 'react-bootstrap';
import relocationApi from '../services/relocationApi';
import { formatDate, formatShortDateTime } from '../utils/dateFormatter';
import {
  RELOCATION_STATE_LABELS,
  RELOCATION_OLD_CODE_AR,
  RELOCATION_OUTSTANDING_AR,
  RELOCATION_CODE_RUNG,
  RELOCATION_CODE_PENDING_AR,
  RELOCATION_UI
} from '../config/relocationLabels';
import '../styles/RelocationStepsPanel.css';

/**
 * «خطوات النقل» — the move, from started to finished.
 * tasks/guest-relocation-v1.md §4.4c · Decisions: GUEST_RELOCATION_DESIGN.md (R4)
 *
 * ── Why this exists ───────────────────────────────────────────────────────────────────────────
 * Without it a move gets STARTED and then sits: the unit is off sale, a code has been queued, an
 * owner is owed a night, and nobody can say what is left. The record already carried all of that;
 * there was simply no way to read it or move it on.
 *
 * ── One button, and it is always the next one (R4) ────────────────────────────────────────────
 * The ladder is forward-only, one step at a time, because every skip available on it skips
 * something safety-critical — a code never cut, an old code never revoked. So the panel offers
 * exactly one action: the step that comes next. Earlier steps are facts with a time on them; later
 * ones are greyed and unpressable rather than hidden, because an operator deciding whether to start
 * a move needs to see what it commits them to.
 *
 * ── What is TRUE, never an instruction ────────────────────────────────────────────────────────
 * Every label reads «تم إصدار رمز جديد», not «أصدر رمزاً». The operator is recording something that
 * has already happened; a button phrased as an order invites a press before the thing is done, and
 * there is no undo.
 *
 * ── The two things that are not steps ─────────────────────────────────────────────────────────
 * The old code and the Gathern block are FACTS about the world, not rungs on the ladder, and they
 * are shown separately for that reason. The Gathern block gates `complete` (L4 + R6) — the panel
 * says so rather than presenting a button that silently refuses.
 */

// The ladder, in order. `held` is set when the move is recorded, so it is always already done.
const STEPS = ['held', 'code_issued', 'guest_informed', 'moved', 'old_code_revoked', 'complete'];

// Cancelling means "this move never happened". Once the guest is physically in the receiving unit
// that is no longer true — the way back is a move-back, not a cancel that erases nights they slept.
const CANCELLABLE = ['held', 'code_issued', 'guest_informed'];

// The old-code states that are OUTSTANDING WORK. `verified` is done and `not_applicable` means
// there was no code to revoke — both routine, and a warning that fires on a normal outcome teaches
// people to stop reading it.
//
// `deferred` IS outstanding: it now survives only when the guest is still in the original unit on a
// move dated later, and nothing in V1 comes back on the day, so its code is live until a person
// kills it.
//
// ⚠️ Must stay in step with RelocationBanner's own list. Two surfaces on one page reading one
// allocation and disagreeing about whether something is wrong is worse than either being silent.
// `already_moved` is outstanding for the sharpest reason of the four: no revoke was ever attempted,
// so nothing at all is known about the code on the unit the guest left.
const OLD_CODE_OUTSTANDING = ['requested', 'acknowledged', 'failed', 'deferred', 'already_moved'];

/**
 * D-R3 — what the code rung is allowed to say, from what the receiving door actually holds.
 *
 * The backend answers `live` / `manual` / `pending` (`resolveNewCodeState`), and REFUSES the write
 * on `pending`, so this is the readable half of a guard rather than the guard itself. Absent — an
 * older payload, or a lookup that failed — is treated as `pending`: the cautious end, because the
 * whole defect was a rung that defaulted to assertable.
 */
const codeRungFor = (allocation) => {
  const state = allocation?.newCodeState?.state || 'pending';
  return {
    state,
    label: RELOCATION_CODE_RUNG[state] || RELOCATION_CODE_RUNG.pending,
    waiting: state === 'pending',
    why: state === 'pending'
      ? (RELOCATION_CODE_PENDING_AR[allocation?.newCodeState?.reason] || RELOCATION_CODE_PENDING_AR.queued)
      : null
  };
};

/**
 * The recorded statuses under which the code rung was a MANUAL KEY HANDOVER, not a code.
 *
 * ⚠️ Read from `newCode.status`, which is written once when the move is made and never revised —
 * deliberately NOT from the live `newCodeState`. A step already ticked must keep saying what the
 * operator confirmed: if a code appeared on that door later, a live-derived label would silently
 * rewrite «تم تسليم المفتاح للضيف يدوياً» into «تم إصدار رمز جديد» and the record would describe
 * something nobody did.
 */
const MANUAL_HANDOVER = ['no_lock', 'not_applicable', 'failed'];

const RelocationStepsPanel = ({ bookingId, isCoreStaff, version, onChanged, onRelocateAgain }) => {
  const [allocation, setAllocation] = useState(null);
  const [loading, setLoading] = useState(true);
  // WHICH action is running, not merely that one is. A single boolean disabled every button and
  // spun a different one from the one that was pressed, which reads as the wrong thing happening.
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  /**
   * Fetch only. It touches NO state and it throws — every caller below decides for itself whether
   * a result is still wanted and what a failure means, which is the whole point: the two callers
   * need opposite behaviour on failure and a shared one got both wrong.
   */
  const fetchActive = useCallback(async () => {
    const data = await relocationApi.list(bookingId);
    // Newest first from the API. The live move is the newest one nobody called off; a completed
    // move stays on screen as the record of what happened to this booking.
    return (data?.allocations || []).find(a => a.state !== 'cancelled') || null;
  }, [bookingId]);

  // ── Opening the page, or switching booking ───────────────────────────────────────────────────
  // Clears first: this page is NOT remounted when the booking in the URL changes, so holding the
  // previous allocation while the next one loads would show one booking's move on another
  // booking's page — and every step button on it would act on the wrong record.
  //
  // ⚠️ EVERY setState is behind the `cancelled` guard, including the failure. Without that, a
  // request for booking A that rejects after booking B has already loaded paints A's error over
  // B's panel — and on a booking with no move that error becomes the entire card.
  //
  // `version` is in the deps because the page bumps it when a move is CREATED, which is the one
  // change to this record that does not originate here.
  useEffect(() => {
    if (!isCoreStaff || !bookingId) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAllocation(null);

    fetchActive()
      .then((found) => { if (!cancelled) setAllocation(found); })
      .catch((err) => {
        console.error('Error loading relocation steps:', err);
        // Silence would read as "no move in progress" on a booking that has one — the same wrong
        // answer the banner exists to prevent.
        if (!cancelled) setError(err.response?.data?.error || RELOCATION_UI.loadFailed);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [fetchActive, isCoreStaff, bookingId, version]);

  // ── Doing something ──────────────────────────────────────────────────────────────────────────
  // Refetches rather than trusting its own response: a second operator may have moved the same
  // record on while this page sat open.
  const run = async (action, fn) => {
    setBusy(action);
    setError(null);
    try {
      await fn();
    } catch (err) {
      // Verbatim. «يجب إكمال الخطوات بالترتيب» and «يجب تأكيد إغلاق الوحدة على القناة أولاً» send the
      // operator to two completely different places, and a generic failure sends them to neither.
      setError(err.response?.data?.error || 'تعذّر تنفيذ الإجراء');
      setBusy(null);
      return;
    }

    // Past this line the action HAS happened. The refresh must not be able to un-say that:
    // it does not blank the card while it runs, and if it fails the step stays ticked and we ask
    // for a reload — showing a load error over work that succeeded invites a second press.
    try {
      setAllocation(await fetchActive());
    } catch (err) {
      console.error('Error refreshing relocation steps:', err);
      setError(RELOCATION_UI.refreshFailed);
    } finally {
      setBusy(null);
    }
    onChanged?.();
  };

  if (!isCoreStaff || loading) return null;
  if (!allocation && !error) return null;

  if (!allocation) {
    return <Alert variant="warning" className="mb-3">{error}</Alert>;
  }

  const currentIndex = STEPS.indexOf(allocation.state);
  // A state this panel does not know about offers no button at all. Unreachable today — `cancelled`
  // is the only other enum value and it is filtered out above — but the failure mode if the ladder
  // ever gains a rung is a live button whose press the backend rejects, and silence beats that.
  const nextState = currentIndex < 0 ? null : (STEPS[currentIndex + 1] || null);
  const complete = allocation.state === 'complete';
  // The manual Gathern step was removed 2026-08-08 — nothing gates `complete` any more. Reasoning
  // in GUEST_RELOCATION_DESIGN.md: the export feed already carries the block, and a block typed into
  // the Gathern extranet is one nothing ever unwinds.
  const oldCodeStatus = allocation.oldCode?.status || null;
  const codeRung = codeRungFor(allocation);

  // The time a step was recorded, from the allocation's own history.
  const stampFor = (state) => {
    const entry = (allocation.stateHistory || []).find(h => h.to === state);
    return entry?.at ? formatShortDateTime(entry.at) : null;
  };

  return (
    <div className="details-card relocation-steps" dir="rtl">
      {/* h2, like every other card on this page — `.details-card h2` carries the colour, size and
          underline that make a card look like a card here. A custom heading was a card that looked
          almost right, which is worse than one that looks different on purpose. */}
      <h2>
        {RELOCATION_UI.steps}
        {complete && <span className="relocation-steps__done">{RELOCATION_UI.done}</span>}
      </h2>

      <p className="relocation-steps__route">
        {/* The backend's label, code first — the same shape the arrivals board and the banner use.
            Not `.name`: two units in one building can differ by a bracketed digit, and this line is
            the operator's record of which door they left and which they went to. */}
        {RELOCATION_UI.moveSummary} «{allocation.originPropertyLabel || allocation.originProperty?.name || '—'}» {RELOCATION_UI.moveSummaryTo} «{allocation.propertyLabel || allocation.property?.name || '—'}»
        {' · '}{formatDate(allocation.from)} — {formatDate(allocation.to)}
      </p>

      {!complete && <p className="relocation-steps__caution">{RELOCATION_UI.noUndo}</p>}

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <ol className="relocation-steps__list">
        {STEPS.map((state, i) => {
          const isDone = i <= currentIndex;
          const isNext = state === nextState;
          const stamp = isDone ? stampFor(state) : null;
          // D-R3 — the code rung is the one step whose wording depends on what the receiving door
          // actually holds. Every other rung means the same thing on every move.
          const isCodeRung = state === 'code_issued';
          // Only while it is the live step. Once it is behind us the record should read as what was
          // recorded at the time, not re-narrate itself from today's lock state.
          const heldForCode = isCodeRung && isNext && codeRung.waiting;

          return (
            <li
              key={state}
              className={`relocation-steps__step ${isDone ? 'is-done' : ''} ${isNext ? 'is-next' : ''} ${heldForCode ? 'is-waiting' : ''}`}
            >
              <i className={`bi ${isDone ? 'bi-check-circle-fill' : 'bi-circle'} relocation-steps__mark`} aria-hidden="true"></i>
              <span className="relocation-steps__label">
                {/* Live wording while it is the step in front of the operator; the wording they
                    actually confirmed once it is behind them. */}
                {isCodeRung
                  ? (isNext
                    ? codeRung.label
                    : (isDone && MANUAL_HANDOVER.includes(allocation.newCode?.status)
                      ? RELOCATION_CODE_RUNG.manual
                      : RELOCATION_STATE_LABELS[state]))
                  : RELOCATION_STATE_LABELS[state]}
              </span>
              {stamp && <span className="relocation-steps__stamp">{stamp}</span>}
              {/* Waiting, not failing. No button — there is nothing an operator can truthfully
                  record yet — and the reason, because "waiting a minute for the worker" and
                  "waiting until the move's start date" are answered differently. */}
              {heldForCode && <span className="relocation-steps__waiting">{codeRung.why}</span>}
              {isNext && !heldForCode && (
                <Button
                  className="touch-btn-sm"
                  variant="primary"
                  disabled={Boolean(busy)}
                  // The step is named on the row, but a screen reader reaching the button alone
                  // would hear «تسجيل» with no subject.
                  // Must be the sentence the operator can SEE on the row — on the code rung that is
                  // the handover wording, not «تم إصدار رمز جديد».
                  aria-label={`${RELOCATION_UI.record}: ${isCodeRung ? codeRung.label : RELOCATION_STATE_LABELS[state]}`}
                  onClick={() => run('step', () => relocationApi.advance(allocation._id, state))}
                >
                  {/* Text stays while it spins — swapping it for a bare spinner erases the button's
                      accessible name at the exact moment something is happening. */}
                  {RELOCATION_UI.record}
                  {busy === 'step' && <Spinner animation="border" size="sm" role="status" className="relocation-steps__spin" />}
                </Button>
              )}
            </li>
          );
        })}
      </ol>

      {/* ── Not steps: facts about the world ─────────────────────────────────────────────────── */}

      {OLD_CODE_OUTSTANDING.includes(oldCodeStatus) && (
        <p className={`relocation-steps__fact ${oldCodeStatus === 'failed' ? 'is-bad' : 'is-warn'}`}>
          <i className="bi bi-key" aria-hidden="true"></i>
          <span className="relocation-steps__label">
            {/* Falls back to an Arabic sentence, never the raw enum key: a new status added to the
                model must not put an English word inside an RTL Arabic card. */}
            {RELOCATION_OLD_CODE_AR[oldCodeStatus] || RELOCATION_OUTSTANDING_AR.oldCodeUnverified}
          </span>
          {/* Names its own source, because the step above it can be ticked while this still says
              "not confirmed" — and both are true. The tick is a PERSON saying they revoked it; this
              is the LOCK's answer. Without the attribution the card looks like it contradicts
              itself, and an operator resolves that by trusting the tick. */}
          <span className="relocation-steps__source">{RELOCATION_UI.oldCodeSource}</span>
        </p>
      )}

      {/* ── Calling it off, or moving them again ──────────────────────────────────────────────
          Exactly one of the two is always offered, never neither. Before the guest has physically
          moved, cancelling is the way out (it re-opens the unit and brings the credential back).
          After, the only route anywhere is another move — and that button used to be missing
          entirely, so the hint below named a way back that ops could not take. */}
      {(
        CANCELLABLE.includes(allocation.state) ? (
          cancelOpen ? (
            <div className="relocation-steps__cancel">
              <p className="relocation-steps__hint">{RELOCATION_UI.cancelWarning}</p>
              <Form.Label htmlFor="relocation-cancel-reason">{RELOCATION_UI.cancelReason}</Form.Label>
              <Form.Control
                id="relocation-cancel-reason"
                as="textarea"
                rows={2}
                maxLength={500}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
              <div className="relocation-steps__cancel-actions">
                <Button
                  className="touch-btn-sm"
                  variant="danger"
                  disabled={Boolean(busy)}
                  onClick={() => run('cancel', async () => {
                    await relocationApi.cancel(allocation._id, cancelReason || undefined);
                    setCancelOpen(false);
                    setCancelReason('');
                  })}
                >
                  {RELOCATION_UI.cancelConfirm}
                  {busy === 'cancel' && <Spinner animation="border" size="sm" role="status" className="relocation-steps__spin" />}
                </Button>
                <Button className="touch-btn-sm" variant="secondary" disabled={Boolean(busy)} onClick={() => setCancelOpen(false)}>
                  {RELOCATION_UI.cancelBack}
                </Button>
              </div>
            </div>
          ) : (
            <Button className="touch-btn-sm" variant="outline-danger" disabled={Boolean(busy)} onClick={() => setCancelOpen(true)}>
              {RELOCATION_UI.cancelMove}
            </Button>
          )
        ) : (
          <div className="relocation-steps__again">
            {/* Only while the move is still in progress. On a completed move nobody is looking for
                a cancel button, and saying "cannot be cancelled" would answer a question the
                operator did not ask. */}
            {!complete && <p className="relocation-steps__hint">{RELOCATION_UI.cancelBlocked}</p>}
            {onRelocateAgain && (
              <Button className="touch-btn-sm" variant="outline-primary" disabled={Boolean(busy)} onClick={onRelocateAgain}>
                <i className="bi bi-box-arrow-right"></i> {RELOCATION_UI.relocateAgain}
              </Button>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default RelocationStepsPanel;
